<template>
  <div class="flex flex-col gap-2 w-full">
    <label v-if="label" :class="labelClass"
      >{{ label }}<span v-if="required" class="text-red-600"> *</span></label
    >
    <div class="relative" ref="triggerRef">
      <button
        type="button"
        class="flex items-center justify-between w-full px-[13px] py-[8px] border rounded-[6px] shadow-sm bg-white text-base leading-[20px] focus:outline-none transition-all duration-200 border-gray-300 hover:border-[#d0a444] focus:ring-2 focus:ring-[#d0a444]/20 focus:border-[#d0a444]"
        :class="{
          'border-red-300 focus:border-red-500 focus:ring-red-200': !!error,
        }"
        @click="toggle"
        @keydown.down.prevent="openAndFocusGrid()"
        @keydown.enter.prevent="toggle"
        @keydown.space.prevent="toggle"
        aria-haspopup="dialog"
        :aria-expanded="open"
        :aria-controls="open ? panelId : undefined"
      >
        <span :class="displayValue ? 'text-gray-900' : 'text-gray-500'">{{
          displayValue || placeholder || "JJ/MM/AAAA"
        }}</span>
        <svg
          class="w-2.5 h-2.5 text-primary transition-transform duration-200"
          :class="{ 'rotate-180': open }"
          viewBox="0 0 10 6"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M0.292893 0.292893C0.683416 -0.097631 1.31658 -0.097631 1.7071 0.292893L4.99999 3.58579L8.29288 0.292893C8.6834 -0.0976311 9.31657 -0.0976311 9.70709 0.292893C10.0976 0.683417 10.0976 1.31658 9.70709 1.70711L5.7071 5.70711C5.31657 6.09763 4.68341 6.09763 4.29289 5.70711L0.292893 1.70711C-0.0976309 1.31658 -0.0976309 0.683417 0.292893 0.292893Z"
            fill="currentColor"
          />
        </svg>
      </button>
      <Transition
        enter-active-class="transition duration-150 ease-out"
        enter-from-class="opacity-0 -translate-y-1"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 -translate-y-1"
      >
        <p v-if="error" class="mt-1 text-[10px] sm:text-xs text-red-600">
          {{ error }}
        </p>
      </Transition>

      <teleport to="body">
        <Transition
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="transform scale-95 opacity-0"
          enter-to-class="transform scale-100 opacity-100"
          leave-active-class="transition duration-150 ease-in"
          leave-from-class="transform scale-100 opacity-100"
          leave-to-class="transform scale-95 opacity-0"
        >
          <div
            v-if="open"
            ref="panelRef"
            class="bg-white border border-gray-200 rounded-md shadow-lg w-72 max-w-[calc(100vw-32px)] p-3 outline-none"
            :class="props.highZIndex ? 'z-[10010]' : 'z-40'"
            :style="floatingStyle"
            role="dialog"
            :id="panelId"
            aria-modal="true"
            @keydown.esc.prevent="close"
          >
            <div class="flex items-center justify-between mb-2 gap-2">
              <button
                class="p-1 rounded hover:bg-gray-100 flex-shrink-0"
                @click="prevMonth"
                aria-label="Mois précédent"
              >
                <svg class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    d="M12.707 14.707a1 1 0 01-1.414 0L6.586 10l4.707-4.707a1 1 0 111.414 1.414L9.414 10l3.293 3.293a1 1 0 010 1.414z"
                  />
                </svg>
              </button>

              <div class="flex items-center gap-1 flex-1 justify-center">
                <!-- Month Dropdown -->
                <div class="relative">
                  <button
                    type="button"
                    @click.stop="toggleMonthDropdown"
                    class="px-2 py-1 text-sm font-medium text-gray-800 bg-white border border-gray-300 rounded hover:border-[#d0a444] focus:outline-none focus:ring-2 focus:ring-[#d0a444]/20 focus:border-[#d0a444] transition-all duration-200 min-w-[100px] flex items-center justify-between"
                  >
                    <span>{{ monthNames[viewMonth] }}</span>
                    <svg
                      class="w-3 h-3 ml-1 text-gray-500 transition-transform duration-200"
                      :class="{ 'rotate-180': showMonthDropdown }"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </button>
                  <div
                    v-if="showMonthDropdown"
                    class="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto z-50 w-[120px]"
                  >
                    <button
                      v-for="(month, idx) in monthNames"
                      :key="idx"
                      type="button"
                      @click="selectMonth(idx)"
                      class="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors"
                      :class="{
                        'bg-[#d0a444]/10 text-[#d0a444] font-medium':
                          viewMonth === idx,
                      }"
                    >
                      {{ month }}
                    </button>
                  </div>
                </div>

                <!-- Year Dropdown -->
                <div class="relative">
                  <button
                    type="button"
                    @click.stop="toggleYearDropdown"
                    class="px-2 py-1 text-sm font-medium bg-white border border-gray-300 rounded hover:border-[#d0a444] focus:outline-none focus:ring-2 focus:ring-[#d0a444]/20 focus:border-[#d0a444] transition-all duration-200 min-w-[70px] flex items-center justify-between"
                    :class="
                      viewYear === null ? 'text-gray-400' : 'text-gray-800'
                    "
                  >
                    <span>{{ yearDisplayText }}</span>
                    <svg
                      class="w-3 h-3 ml-1 text-gray-500 transition-transform duration-200"
                      :class="{ 'rotate-180': showYearDropdown }"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </button>
                  <div
                    v-if="showYearDropdown"
                    class="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto z-50 w-[90px]"
                  >
                    <button
                      v-for="year in yearOptions"
                      :key="year"
                      type="button"
                      @click="selectYear(year)"
                      class="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors"
                      :class="{
                        'bg-[#d0a444]/10 text-[#d0a444] font-medium':
                          viewYear === year,
                      }"
                    >
                      {{ year }}
                    </button>
                  </div>
                </div>
              </div>

              <button
                class="p-1 rounded hover:bg-gray-100 flex-shrink-0"
                @click="nextMonth"
                aria-label="Mois suivant"
              >
                <svg class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    d="M7.293 5.293a1 1 0 011.414 0L13.414 10l-4.707 4.707a1 1 0 01-1.414-1.414L10.586 10 7.293 6.707a1 1 0 010-1.414z"
                  />
                </svg>
              </button>
            </div>

            <div class="grid grid-cols-7 gap-1 text-[11px] text-gray-500 mb-1">
              <div v-for="d in weekdays" :key="d" class="text-center">
                {{ d }}
              </div>
            </div>
            <div
              class="grid grid-cols-7 gap-1"
              role="grid"
              @keydown.stop="onGridKeydown"
              @click="closeDropdowns"
            >
              <button
                v-for="(cell, idx) in cells"
                :key="idx"
                type="button"
                class="h-8 text-xs rounded flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[#d0a444]/30"
                :class="cellClass(cell)"
                :disabled="cell.disabled"
                :aria-selected="cell.selected ? 'true' : 'false'"
                :aria-label="
                  cell.disabled || !cell.date
                    ? ''
                    : cell.date.toLocaleDateString('fr-FR')
                "
                @pointerdown.stop.prevent="
                  cell.isCurrentMonth ? selectDateSafe(cell.date) : void 0
                "
                @mousedown.stop.prevent="
                  cell.isCurrentMonth ? selectDateSafe(cell.date) : void 0
                "
                @click.stop.prevent="
                  cell.isCurrentMonth ? selectDateSafe(cell.date) : void 0
                "
                @keydown.enter.prevent="
                  cell.isCurrentMonth ? selectDateSafe(cell.date) : void 0
                "
                @keydown.space.prevent="
                  cell.isCurrentMonth ? selectDateSafe(cell.date) : void 0
                "
              >
                {{ cell.label }}
              </button>
            </div>

            <!-- Time picker (only when withTime prop is set) -->
            <template v-if="props.withTime">
              <div class="mt-3 pt-3 border-t border-gray-100">
                <p
                  class="text-[10px] text-gray-400 uppercase tracking-wide mb-2 text-center"
                >
                  Heure
                </p>
                <div class="flex items-center justify-center gap-2">
                  <!-- Hour select -->
                  <select
                    v-model.number="selectedHour"
                    class="w-16 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#d0a444]/20 focus:border-[#d0a444] text-center bg-white"
                    @click.stop
                  >
                    <option v-for="h in hours" :key="h" :value="h">
                      {{ String(h).padStart(2, "0") }}
                    </option>
                  </select>
                  <span class="text-gray-500 font-semibold text-sm">:</span>
                  <!-- Minute select -->
                  <select
                    v-model.number="selectedMinute"
                    class="w-16 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#d0a444]/20 focus:border-[#d0a444] text-center bg-white"
                    @click.stop
                  >
                    <option v-for="m in minutes" :key="m" :value="m">
                      {{ String(m).padStart(2, "0") }}
                    </option>
                  </select>
                </div>
              </div>

              <!-- Confirm button (required when withTime to finalise selection) -->
              <div class="mt-3">
                <button
                  type="button"
                  :disabled="!pendingDate"
                  @click.stop="confirmDateTime"
                  class="w-full py-1.5 rounded-md text-sm font-medium transition-colors"
                  :class="
                    pendingDate
                      ? 'bg-[#d0a444] text-white hover:bg-[#b8903d]'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  "
                >
                  Confirmer
                </button>
              </div>
            </template>
          </div>
        </Transition>
      </teleport>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onBeforeUnmount } from "vue";
import { useRoute } from "vue-router";

const props = withDefaults(
  defineProps<{
    modelValue?: string | null;
    label?: string;
    placeholder?: string;
    required?: boolean;
    minDate?: Date;
    maxDate?: Date;
    disabledDates?: string[]; // Array of dates in YYYY-MM-DD format to disable
    valueFormat?: "yyyy-MM-dd" | string;
    displayFormat?: "dd/MM/yyyy" | string;
    error?: string;
    highZIndex?: boolean;
    initialViewDate?: Date; // Control initial calendar month/year when no value selected
    labelClass?: string;
    withTime?: boolean; // Enable time (hour + minute) selection
  }>(),
  {
    modelValue: null,
    disabledDates: () => [],
    required: false,
    valueFormat: "yyyy-MM-dd",
    displayFormat: "dd/MM/yyyy",
    highZIndex: false,
    labelClass: "text-md font-medium text-gray-700 block leading-6",
    withTime: false,
  },
);

const emit = defineEmits<{
  "update:modelValue": [value: string | null];
  change: [value: string | null];
  blur: [];
}>();

const route = useRoute();

const open = ref(false);
const showMonthDropdown = ref(false);
const showYearDropdown = ref(false);
const triggerRef = ref<HTMLElement | null>(null);
const panelRef = ref<HTMLElement | null>(null);
const floatingStyle = ref<{ position: "fixed"; left: string; top: string }>({
  position: "fixed",
  left: "0px",
  top: "0px",
});

const today = new Date();
const viewYear = ref<number | null>(null);
const viewMonth = ref<number>(today.getMonth());

// Time picker state (used when withTime = true)
const pendingDate = ref<Date | null>(null); // date clicked but not yet confirmed
const selectedHour = ref(0);
const selectedMinute = ref(0);

const hours = Array.from({ length: 24 }, (_, i) => i);
const minutes = Array.from({ length: 12 }, (_, i) => i * 5);

const weekdays = ["Lu", "Ma", "Me", "Je", "Ve", "Sa", "Di"];

const monthNames = [
  "Janvier",
  "Février",
  "Mars",
  "Avril",
  "Mai",
  "Juin",
  "Juillet",
  "Août",
  "Septembre",
  "Octobre",
  "Novembre",
  "Décembre",
];

// Generate year options based on minDate and maxDate props
const yearOptions = computed(() => {
  const minYear = props.minDate?.getFullYear() ?? today.getFullYear() - 100;
  const maxYear = props.maxDate?.getFullYear() ?? today.getFullYear() + 10;
  const years = [];
  for (let year = minYear; year <= maxYear; year++) {
    years.push(year);
  }
  // Sort in descending order (most recent first) for better UX
  return years.reverse();
});

const selectedDate = computed<Date | null>(() => parseValue(props.modelValue));
const displayValue = computed(() =>
  selectedDate.value ? formatDisplay(selectedDate.value) : "",
);

const monthLabel = computed(() => {
  const year = viewYear.value ?? today.getFullYear();
  const d = new Date(year, viewMonth.value, 1);
  return d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
});

const yearDisplayText = computed(() => {
  if (viewYear.value === null) {
    return "Année";
  }
  return viewYear.value.toString();
});

function initialDate(): Date {
  return parseValue(props.modelValue) || props.initialViewDate || today;
}

function daysInMonth(year: number | null, month: number) {
  const actualYear = year ?? today.getFullYear();
  return new Date(actualYear, month + 1, 0).getDate();
}

function startWeekday(year: number | null, month: number) {
  const actualYear = year ?? today.getFullYear();
  const w = new Date(actualYear, month, 1).getDay(); // 0=Sun
  // Make Monday first: convert Sun=0 to 6, others -1
  return w === 0 ? 6 : w - 1;
}

const cells = computed(() => {
  const actualYear = viewYear.value ?? today.getFullYear();
  const totalDays = daysInMonth(viewYear.value, viewMonth.value);
  const start = startWeekday(viewYear.value, viewMonth.value);
  const arr: {
    date: Date | null;
    label: number;
    disabled: boolean;
    current: boolean;
    selected: boolean;
    isCurrentMonth: boolean;
  }[] = [];

  // Empty cells for days before month start
  for (let i = 0; i < start; i++) {
    // Calculate the actual date from previous month
    const prevMonthDate = new Date(
      actualYear,
      viewMonth.value,
      -(start - i - 1),
    );
    // Check if this date should be disabled due to min/max constraints
    const minBlocked = props.minDate
      ? prevMonthDate < normalize(props.minDate)
      : false;
    const maxBlocked = props.maxDate
      ? prevMonthDate > normalize(props.maxDate)
      : false;
    const disabledByList = isDateInDisabledList(prevMonthDate);
    const constraintDisabled = Boolean(
      minBlocked || maxBlocked || disabledByList,
    );

    arr.push({
      date: prevMonthDate,
      label: prevMonthDate.getDate(),
      disabled: constraintDisabled,
      current: false,
      selected: false,
      isCurrentMonth: false,
    });
  }

  // Actual month days
  for (let d = 1; d <= totalDays; d++) {
    const date = new Date(actualYear, viewMonth.value, d);
    const minBlocked = props.minDate ? date < normalize(props.minDate) : false;
    const maxBlocked = props.maxDate ? date > normalize(props.maxDate) : false;
    const disabledByList = isDateInDisabledList(date);
    const disabled = Boolean(minBlocked || maxBlocked || disabledByList);
    const current = isSameDate(date, today);
    const selected = selectedDate.value
      ? isSameDate(date, selectedDate.value)
      : false;
    arr.push({
      date,
      label: d,
      disabled,
      current,
      selected,
      isCurrentMonth: true,
    });
  }

  // Fill remaining cells with next month days
  const totalCells = 42; // 6 rows * 7 days
  const remainingCells = totalCells - arr.length;
  for (let i = 1; i <= remainingCells; i++) {
    const nextMonthDate = new Date(actualYear, viewMonth.value + 1, i);
    // Check if this date should be disabled due to min/max constraints
    const minBlocked = props.minDate
      ? nextMonthDate < normalize(props.minDate)
      : false;
    const maxBlocked = props.maxDate
      ? nextMonthDate > normalize(props.maxDate)
      : false;
    const disabledByList = isDateInDisabledList(nextMonthDate);
    const constraintDisabled = Boolean(
      minBlocked || maxBlocked || disabledByList,
    );

    arr.push({
      date: nextMonthDate,
      label: nextMonthDate.getDate(),
      disabled: constraintDisabled,
      current: false,
      selected: false,
      isCurrentMonth: false,
    });
  }
  return arr;
});

function normalize(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function isSameDate(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatValue(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  if (props.withTime) {
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  }
  return `${yyyy}-${mm}-${dd}`;
}

// Helper function to check if a date is disabled via the disabledDates prop
function isDateInDisabledList(date: Date): boolean {
  const dateString = formatValue(date);
  return props.disabledDates.includes(dateString);
}

function formatDisplay(d: Date): string {
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  if (props.withTime) {
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
  }
  return `${dd}/${mm}/${yyyy}`;
}

function parseValue(val?: string | null): Date | null {
  if (!val) return null;
  // Handle datetime: YYYY-MM-DDTHH:MM
  const mdt = /^([0-9]{4})-([0-9]{2})-([0-9]{2})T([0-9]{2}):([0-9]{2})/.exec(
    val,
  );
  if (mdt) {
    const d = new Date(
      Number(mdt[1]),
      Number(mdt[2]) - 1,
      Number(mdt[3]),
      Number(mdt[4]),
      Number(mdt[5]),
    );
    return isNaN(d.getTime()) ? null : d;
  }
  // Handle date only: YYYY-MM-DD
  const m = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(val);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(d.getTime()) ? null : d;
}

const panelId = `datepicker-panel-${Math.random().toString(36).slice(2, 8)}`;

function toggle() {
  if (open.value) {
    close();
  } else {
    // Initialize view year if not set (first time opening without a date)
    if (viewYear.value === null) {
      if (props.modelValue) {
        viewYear.value = initialDate().getFullYear();
        viewMonth.value = initialDate().getMonth();
      } else {
        // Set to the most recent allowed year for better UX
        const maxYear =
          props.maxDate?.getFullYear() ?? today.getFullYear() + 10;
        viewYear.value = maxYear;
        viewMonth.value = today.getMonth();
      }
    }
    openPanel();
  }
}

function openPanel() {
  // Sync view to selected date or today each time we open
  const base = selectedDate.value || initialDate() || today;
  viewYear.value = base.getFullYear();
  viewMonth.value = base.getMonth();
  // Initialise time selectors from existing value
  if (props.withTime) {
    const existing = parseValue(props.modelValue);
    selectedHour.value = existing ? existing.getHours() : 0;
    selectedMinute.value = existing ? existing.getMinutes() : 0;
    pendingDate.value = existing
      ? new Date(
          existing.getFullYear(),
          existing.getMonth(),
          existing.getDate(),
        )
      : null;
  }
  open.value = true;
  nextTick(() => {
    position();
    document.addEventListener("click", onGlobalClick, true);
    document.addEventListener("keydown", onGlobalKey, true);
    window.addEventListener("scroll", position, true);
    window.addEventListener(
      "resize",
      position as any,
      { passive: true } as any,
    );
  });
}

function close() {
  if (!open.value) return;
  open.value = false;
  showMonthDropdown.value = false;
  showYearDropdown.value = false;
  pendingDate.value = null;
  document.removeEventListener("click", onGlobalClick, true);
  document.removeEventListener("keydown", onGlobalKey, true);
  window.removeEventListener("scroll", position, true);
  window.removeEventListener("resize", position);
  queueMicrotask(() => emit("blur"));
}

function toggleMonthDropdown() {
  showMonthDropdown.value = !showMonthDropdown.value;
  if (showMonthDropdown.value) {
    showYearDropdown.value = false;
  }
}

function toggleYearDropdown() {
  showYearDropdown.value = !showYearDropdown.value;
  if (showYearDropdown.value) {
    showMonthDropdown.value = false;
  }
}

function selectMonth(month: number) {
  viewMonth.value = month;
  showMonthDropdown.value = false;
}

function selectYear(year: number) {
  viewYear.value = year;
  showYearDropdown.value = false;
}

function closeDropdowns() {
  showMonthDropdown.value = false;
  showYearDropdown.value = false;
}

function position() {
  if (!triggerRef.value || !panelRef.value) return;
  const rect = triggerRef.value.getBoundingClientRect();
  let left = rect.left;
  let top = rect.bottom + 4;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const menuW = panelRef.value.offsetWidth || 288;
  const menuH = panelRef.value.offsetHeight || 320;
  if (left + menuW > vw - 8) left = Math.max(8, vw - menuW - 8);
  if (top + menuH > vh - 8) top = rect.top - 4 - menuH;
  if (top < 8) top = 8;
  floatingStyle.value = {
    position: "fixed",
    left: `${left}px`,
    top: `${top}px`,
  };
}

function onGlobalClick(e: Event) {
  const t = e.target as Node;
  if (triggerRef.value?.contains(t) || panelRef.value?.contains(t)) return;
  close();
}

function onGlobalKey(e: KeyboardEvent) {
  if (e.key === "Escape") close();
}

function selectDate(d: Date) {
  if (
    !d ||
    isNaN(d.getTime()) ||
    (props.minDate && d < normalize(props.minDate)) ||
    (props.maxDate && d > normalize(props.maxDate)) ||
    isDateInDisabledList(d)
  )
    return;

  if (props.withTime) {
    // Don't emit yet — let user pick time then confirm
    pendingDate.value = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    return;
  }

  const v = formatValue(d);
  emit("update:modelValue", v);
  emit("change", v);
  close();
}

function confirmDateTime() {
  const base = pendingDate.value;
  if (!base) return;
  const d = new Date(
    base.getFullYear(),
    base.getMonth(),
    base.getDate(),
    selectedHour.value,
    selectedMinute.value,
  );
  const v = formatValue(d);
  emit("update:modelValue", v);
  emit("change", v);
  close();
}

function selectDateSafe(date: Date | null) {
  if (!date || isNaN(date.getTime())) return;

  // Check if this date belongs to the current view month
  const currentViewMonth = viewMonth.value;
  const currentViewYear = viewYear.value;
  const dateMonth = date.getMonth();
  const dateYear = date.getFullYear();

  if (dateMonth !== currentViewMonth || dateYear !== currentViewYear) {
    // If it's a date from previous/next month, navigate to that month
    viewMonth.value = dateMonth;
    viewYear.value = dateYear;
    return;
  }

  // If it's from current month, select it
  selectDate(date);
}

function prevMonth() {
  // Ensure viewYear is initialized
  if (viewYear.value === null) {
    const maxYear = props.maxDate?.getFullYear() ?? today.getFullYear() + 10;
    viewYear.value = maxYear;
  }

  if (viewMonth.value === 0) {
    viewMonth.value = 11;
    viewYear.value -= 1;
  } else {
    viewMonth.value -= 1;
  }
}

function nextMonth() {
  // Ensure viewYear is initialized
  if (viewYear.value === null) {
    const maxYear = props.maxDate?.getFullYear() ?? today.getFullYear() + 10;
    viewYear.value = maxYear;
  }

  if (viewMonth.value === 11) {
    viewMonth.value = 0;
    viewYear.value += 1;
  } else {
    viewMonth.value += 1;
  }
}

function openAndFocusGrid() {
  openPanel();
  nextTick(() => {
    const first = panelRef.value?.querySelector<HTMLButtonElement>(
      'div[role="grid"] button:not([disabled])',
    );
    if (first) first.focus();
  });
}

function onGridKeydown(e: KeyboardEvent) {
  const key = e.key;
  if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(key))
    return;
  e.preventDefault();
  const buttons = Array.from(
    panelRef.value?.querySelectorAll<HTMLButtonElement>(
      'div[role="grid"] button',
    ) || [],
  );
  const enabled = buttons.filter((b) => !b.disabled);
  const active = document.activeElement as HTMLButtonElement | null;
  const idx = enabled.indexOf(active as HTMLButtonElement);
  if (idx === -1) {
    enabled[0]?.focus();
    return;
  }
  const delta =
    key === "ArrowLeft"
      ? -1
      : key === "ArrowRight"
        ? 1
        : key === "ArrowUp"
          ? -7
          : 7;
  let nextIdx = Math.min(Math.max(idx + delta, 0), enabled.length - 1);
  enabled[nextIdx]?.focus();
}

watch(
  () => route.fullPath,
  () => close(),
);

// Watch modelValue to sync viewYear when date is selected or cleared
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue) {
      // Date was selected - update viewYear and viewMonth to match
      const date = parseValue(newValue);
      if (date) {
        viewYear.value = date.getFullYear();
        viewMonth.value = date.getMonth();
      }
    } else {
      // Date was cleared - reset viewYear to show placeholder
      viewYear.value = null;
    }
  },
);

onBeforeUnmount(() => close());

function isPendingDate(cell: { date: Date | null }): boolean {
  if (!props.withTime || !pendingDate.value || !cell.date) return false;
  return isSameDate(cell.date, pendingDate.value);
}

function cellClass(cell: {
  date: Date | null;
  disabled: boolean;
  current: boolean;
  selected: boolean;
  isCurrentMonth: boolean;
}) {
  if (!cell.isCurrentMonth) {
    return "text-gray-300 cursor-default";
  }

  return [
    cell.disabled
      ? "text-gray-400 cursor-not-allowed"
      : "text-gray-700 hover:bg-gray-50 cursor-pointer",
    cell.current ? "ring-1 ring-[#d0a444]/40" : "",
    cell.selected || isPendingDate(cell)
      ? "bg-[#d0a444]/10 text-[#d0a444] font-medium"
      : "",
  ].join(" ");
}
</script>

<style scoped>
/* No extra CSS needed; Tailwind used */
</style>
